import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { getCurrentAlbumSlug } from '@/lib/utils';
import * as Sentry from "@sentry/react";
import { safeLogRocketTrack, safeLogRocketIdentify } from '@/lib/utils';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext, type AuthContextType };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


const handleActiveStatus = async (user: User) => {
  try {
    return await supabase
      .from('user_sessions')
      .upsert({
        user_id: user.id,
        last_active_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'Error updating session' }
    });
    console.error('Error updating session:', error);
  }
}

const ensureUserRole = async (user: User, albumId: string) => {
  try {
    // Check if user already has a role for this album
    const { data: existingRole } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('album_id', albumId)
      .single();

    // If no role exists, create a default 'user' role
    if (!existingRole) {
      await supabase
        .from('user_roles')
        .insert({
          user_id: user.id,
          album_id: albumId,
          role: 'user'
        });
    }
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'Error ensuring user role' }
    });
    console.error('Error ensuring user role:', error);
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { albumId, loading: albumLoading } = useAlbumContext();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);


        if (session?.user) {

          safeLogRocketIdentify(session.user.id, {
            name: session.user.user_metadata?.full_name || '',
            email: session.user.email,
            album_name: getCurrentAlbumSlug() || '',
          });

          safeLogRocketTrack("Validación de Auth", {
            user_id: session.user.id,
            album_name: getCurrentAlbumSlug() || '',
          });
        }

        // Ensure user has a role when they sign in (only if we have a valid albumId and album context is loaded)
        if (session?.user && event === 'SIGNED_IN' && albumId && !albumLoading) {
          setTimeout(() => {
            ensureUserRole(session.user, albumId);
          }, 0);
        }

        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Ensure user has a role for existing sessions (only if we have a valid albumId and album context is loaded)
      if (session?.user && albumId && !albumLoading) {
        setTimeout(() => {
          ensureUserRole(session.user, albumId);
        }, 0);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [albumId, albumLoading]);


  // Update active status every 30 seconds while user is logged in
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    const updateStatus = () => {
      if (document.visibilityState !== 'visible') return
      if (user) handleActiveStatus(user)
    }

    // Trigger initial update
    updateStatus()

    // Set interval
    if (user) {
      intervalId = setInterval(updateStatus, 30_000)
    }

    // Optional: update immediately when returning to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateStatus()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (intervalId) clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user])

  const signInWithGoogle = async () => {
    let albumSlug = getCurrentAlbumSlug();

    // Get the current location to preserve return path and query params
    const currentPath = window.location.pathname + window.location.search;
    const returnPath = currentPath !== `/${albumSlug}/login` ? currentPath : `/${albumSlug}/inicio`;

    const redirectUrl = albumSlug
      ? `${window.location.origin}${returnPath}`
      : `${window.location.origin}/`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl
      }
    });

    if (error) {
      throw error;
    }
  };

  // Sign Out
  const signOut = async () => {
    try {

      // track logout
      safeLogRocketTrack("Logout", {
        user_id: session.user.id,
        album_name: getCurrentAlbumSlug() || '',
      });

      // Clear local state first
      setUser(null);
      setSession(null);

      // Clean up all album-specific auth storage keys
      const albumSlug = getCurrentAlbumSlug();
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('bk:') && key.includes('supabase')) {
          localStorage.removeItem(key);
        }
      });

      // Sign out from Supabase with global scope to clear all tokens
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        Sentry.captureException(error, {
          extra: { context: 'Sign out error' }
        });
        console.error('Sign out error:', error);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'Error during sign out' }
      });
      console.error('Error during sign out:', error);
    } finally {
      // Always redirect regardless of errors


      const redirectUrl = getCurrentAlbumSlug()
        ? `${window.location.origin}/${getCurrentAlbumSlug()}/inicio`
        : `${window.location.origin}/`;

      window.location.href = redirectUrl;
    }
  };

  const value = {
    user,
    session,
    loading,
    signInWithGoogle,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};